import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    defaultOperationDate,
    FormDateField,
    FormNumberField,
    FormSelect,
    FormTextarea,
    FormTextField,
    SuggestNameButton,
    toOperationTimestamp,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import SomethingHappened from '@/components/SomethingHappened';
import { useGetFishTypes } from '@/domain/cages';
import { type CreateFishGroupRequest, useCreateFishGroup } from '@/domain/fishGroups';
import { translateDataLabel } from '@/i18n/dataLabels';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    cage: { id: number; name: string; locationName: string } | null;
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    name: string;
    // `fishTypes.id` as the select's string value; '' until the user picks one.
    // The group owns its species, so nothing is preselected from the cage.
    fishTypeId: string;
    initialPopulationSize: string;
    startDate: Date | null;
    reason: string;
    notes: string;
}

// A factory, not a constant: `startDate` defaults to today, and a module-level
// constant would freeze "today" for the lifetime of the tab.
function makeDefaultValues(): FormValues {
    return {
        name: '',
        fishTypeId: '',
        initialPopulationSize: '',
        startDate: defaultOperationDate(),
        reason: '',
        notes: '',
    };
}

export default function CreateFishGroupModal(props: Props) {
    const { clientId, cage, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    // Species names are stored data, translated under the default namespace.
    const { t: tLegacy } = useTranslation();
    const fishTypesQuery = useGetFishTypes();
    const { data: fishTypes } = fishTypesQuery;
    // Without the list there is nothing to pick, so the required field could
    // never be met: say the lookup failed and offer a retry instead.
    const fishTypesFailed = Boolean(fishTypesQuery.error) && fishTypes.length === 0;
    const fishTypeOptions = fishTypes.map((fishType) => ({
        value: String(fishType.id),
        label: translateDataLabel(tLegacy, 'fishTypes', fishType.name),
    }));
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: makeDefaultValues() });

    const handleClose = () => {
        if (isSubmitting) return;
        reset(makeDefaultValues());
        onClose();
    };

    const {
        create,
        error: serverError,
        reset: resetMutation,
    } = useCreateFishGroup(clientId, {
        onSuccess: () => {
            ToastSuccess(t('actions.create.successToast'), 'fish-group-create-success');
            reset(makeDefaultValues());
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    const onSubmit = async (values: FormValues) => {
        if (!cage || !values.startDate || values.fishTypeId === '') return;
        const requestBody: CreateFishGroupRequest = {
            cageId: cage.id,
            name: values.name.trim(),
            fishTypeId: Number(values.fishTypeId),
            ...(values.initialPopulationSize !== ''
                ? { initialPopulationSize: Number(values.initialPopulationSize) }
                : {}),
            startDate: toOperationTimestamp(values.startDate),
            ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        await create(requestBody).catch(() => {
            /* surfaced via serverError */
        });
    };

    const body = cage ? (
        <>
            <p className={styles.contextLine}>
                {t('actions.create.context')}{' '}
                <span className={styles.contextStrong}>{cage.name}</span> ({cage.locationName})
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormTextField
                    control={control}
                    name="name"
                    label={t('actions.fields.name')}
                    rules={{ required: true, minLength: 1 }}
                    labelAction={
                        <SuggestNameButton
                            cageName={cage.name}
                            operation="create"
                            onApply={(value) =>
                                setValue('name', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                })
                            }
                        />
                    }
                />
                {fishTypesFailed ? (
                    <SomethingHappened
                        title={t('actions.fields.fishTypeLoadError')}
                        headingLevel="h3"
                        onRetry={fishTypesQuery.refetch}
                        isRetrying={fishTypesQuery.isFetching}
                    />
                ) : (
                    <FormSelect
                        control={control}
                        name="fishTypeId"
                        label={t('actions.fields.fishType')}
                        placeholder={
                            fishTypesQuery.isLoading
                                ? t('actions.fields.fishTypeLoading')
                                : t('actions.fields.fishTypePlaceholder')
                        }
                        options={fishTypeOptions}
                        rules={{ required: true }}
                    />
                )}
                <FormNumberField
                    control={control}
                    name="initialPopulationSize"
                    label={t('actions.fields.initialPopulationSize')}
                    optional
                    min={1}
                    rules={{
                        validate: (value) => value === '' || Number(value) >= 1 || 'must be >= 1',
                    }}
                />
                <FormDateField
                    control={control}
                    name="startDate"
                    label={t('actions.fields.startDate')}
                    rules={{ required: true }}
                    placeholder={t('actions.fields.startDatePlaceholder')}
                    blockFutureDates
                />
                <FormTextField
                    control={control}
                    name="reason"
                    label={t('actions.fields.reason')}
                    optional
                />
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    ) : null;

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button
                variant="primary"
                type="submit"
                form={formId}
                disabled={isSubmitting || fishTypesFailed}>
                {t('actions.create.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.create.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
